import React, { useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion, useAnimationControls } from 'framer-motion';
import { EASE_OUT, KitError, KitField, KitPrimaryButton } from '../ui/premiumKit';
import { CountUp } from '../vip/VipPlanMotion';
import VisitorReportHyperframe from './VisitorReportHyperframe';
import { MagicStar, SearchNormal1 } from 'iconsax-react';

// Instagram 아이콘 (iconsax-react 버그로 인해 직접 SVG 사용)
const InstagramIcon: React.FC<{ size?: number; color?: string; className?: string }> = ({
    size = 20,
    color = '#71717A',
    className = ''
}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        className={className}
    >
        <path
            d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
            fill={color}
        />
    </svg>
);

interface HeroSectionProps {
    user: any;
    targetUsername: string;
    setTargetUsername: (value: string) => void;
    isAnalyzing: boolean;
    error: string;
    analysisStep: number;
    handleQuickAnalysis: () => void;
    navigate: (path: string) => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
    user,
    targetUsername,
    setTargetUsername,
    isAnalyzing,
    error,
    analysisStep,
    handleQuickAnalysis,
    navigate
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const reduce = useReducedMotion();
    const shake = useAnimationControls();
    const rise = (delay: number) => ({
        initial: reduce ? false : { opacity: 0, y: 14 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, delay, ease: EASE_OUT },
    } as const);
    // 제목 한 줄씩 아래에서 밀려 올라온다(줄 가리개 안에서) — 움직임 줄이기면 바로 보인다
    const line = (delay: number) => ({
        initial: reduce ? false : { y: '108%' },
        animate: { y: '0%' },
        transition: { duration: 0.9, delay, ease: EASE_OUT },
    } as const);
    // 주 단추는 늘 살아 있다. 빈칸이면 입력칸으로 데려가 살짝 흔들고, 안내 문구는 예전 그대로(handleQuickAnalysis가 띄움)
    const onStart = () => {
        if (!targetUsername.trim()) {
            // 옛 화면은 빈칸이면 단추가 꺼져 아무 일도 없었다 — 안내 글은 띄우지 않고 입력칸으로만 데려간다(독립 검토 11/3)
            inputRef.current?.focus();
            if (!reduce) void shake.start({ x: [0, -8, 8, -5, 5, 0], transition: { duration: 0.42 } });
            return;
        }
        handleQuickAnalysis();
    };
    // 지표 숫자는 차오른다(끝 글자는 예전 그대로)
    const statValue = (v: string) => {
        const m = v.match(/^(\d+~)?(\d+)(.*)$/);
        if (!m || m[1]) return v; // '1~2분'은 차오르는 동안 '1~0분'으로 보여 그대로 둔다
        return <>{m[1] || ''}<CountUp to={Number(m[2])} duration={1.1} delay={0.5} />{m[3]}</>;
    };

    // 2026-11-03 디자인만 새로(대표 "이 홈은 안해도되는거야?" → "디자인 거장 같은 느낌은 안 든다 … 포인트를 주거나 세련되게").
    // 기준: 확정 최고 기준(10/14 안내서) — 스튜디오 조명(위에서 비추는 둥근 빛)·제품을 손에 잡히게(떠 있는 폰 + 바닥 그림자)·
    // 살아 있는 흰 캡슐·숫자 차오름. 포인트 두 곳: 제목 둘째 줄을 지나가는 빛, 입력칸을 누르면 켜지는 인스타그램 색 테두리.
    // 글귀와 단추가 하는 일은 예전 그대로.
    return (
        <div className="relative overflow-hidden bg-black lg:min-h-screen">
            {/* 스튜디오 조명 — 위에서 아래로 은은한 둥근 빛(빛 덩어리·번짐 아님) */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{ background: 'radial-gradient(75% 42% at 24% 30%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.015) 50%, rgba(255,255,255,0) 72%)' }}
            />
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 relative z-10" ref={containerRef}>
                <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-12 pt-6 pb-16 sm:pt-10 lg:min-h-[calc(100vh-60px)] lg:py-10">

                    {/* 좌측 — 카피 + 전환 입력 */}
                    <div className="w-full max-w-xl text-left lg:mx-0">
                        {/* 머리글(알약 상자 대신 작은 글씨) */}
                        <motion.div {...rise(0.05)} className="inline-flex items-center gap-2">
                            <MagicStar size={13} variant="Bold" style={{ color: '#A1A1AA' }} />
                            <span className="text-[12px] font-semibold uppercase tracking-[0.2em] text-[#A1A1AA]">Instagram Intelligence</span>
                        </motion.div>

                        <h1
                            className="mt-5 text-[44px] font-bold text-[#FAFAFA] sm:text-[56px] lg:text-[64px]"
                            style={{ lineHeight: 1.06, letterSpacing: '-0.04em' }}
                        >
                            <span className="block overflow-hidden pb-[0.06em]">
                                <motion.span {...line(0.1)} className="block">Instagram</motion.span>
                            </span>
                            <span className="block overflow-hidden pb-[0.1em]">
                                <motion.span {...line(0.22)} className="block sdl-sheen">인사이트 분석</motion.span>
                            </span>
                        </h1>

                        <motion.p {...rise(0.38)} className="mt-5 max-w-[34rem] text-[16px] leading-[1.7] text-[#A1A1AA] sm:text-[18px] break-keep">
                            방문자 흐름부터 팔로워 변화, 게시물 반응까지 —{' '}
                            <span className="font-semibold text-[#FAFAFA]">내 계정의 모든 시그널</span>을 한눈에 확인하세요.{' '}
                            <span className="font-medium text-[#FAFAFA]">VIP플랜 실시간 모니터링</span>으로
                            나를 향한 관심을 놓치지 마세요.
                        </motion.p>

                        {/* 전환 입력 / 로그인 시 대시보드 이동 */}
                        <motion.div {...rise(0.46)} className="mt-8">
                            {user ? (
                                <div className="rounded-[28px] border border-white/[0.08] bg-[#0F0F11] p-5 sm:p-6" style={{ boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 30px 60px -30px rgba(0,0,0,0.9)' }}>
                                    <p className="text-[14px] text-[#A1A1AA]">대시보드에서 새로운 분석을 시작하세요</p>
                                    <div className="mt-4">
                                        <KitPrimaryButton onClick={() => navigate('/dashboard')}>
                                            <span>대시보드로 이동</span>
                                        </KitPrimaryButton>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    {/* 포인트 ①: 입력칸을 누르면 인스타그램 색 테두리가 켜진다 */}
                                    <motion.div animate={shake} className="group relative rounded-2xl">
                                        <div
                                            aria-hidden
                                            className="pointer-events-none absolute -inset-px rounded-[17px] opacity-0 transition-opacity duration-300 group-focus-within:opacity-100"
                                            style={{ background: 'linear-gradient(120deg, #2563EB 0%, #3182F6 40%, #5EA2FF 75%, #A5CDFF 100%)' }}
                                        />
                                        <div
                                            aria-hidden
                                            className="pointer-events-none absolute -inset-2 rounded-[24px] opacity-0 blur-xl transition-opacity duration-500 group-focus-within:opacity-40"
                                            style={{ background: 'linear-gradient(120deg, #2563EB 0%, #3182F6 40%, #5EA2FF 75%, #A5CDFF 100%)' }}
                                        />
                                        <div className="relative">
                                            <KitField icon={<InstagramIcon size={20} color="#A1A1AA" />} className="group-focus-within:border-transparent">
                                                <input
                                                    ref={inputRef}
                                                    type="text"
                                                    value={targetUsername}
                                                    onChange={(e) => setTargetUsername(e.target.value.toLowerCase())}
                                                    placeholder="instagram_id"
                                                    className="w-full bg-transparent text-[18px] font-medium text-[#FAFAFA] placeholder-[#6B6B74] focus:outline-none"
                                                    onKeyDown={(e) => e.key === 'Enter' && onStart()}
                                                    disabled={isAnalyzing}
                                                    autoCapitalize="none"
                                                    autoCorrect="off"
                                                    spellCheck={false}
                                                    enterKeyHint="go"
                                                />
                                            </KitField>
                                        </div>
                                    </motion.div>

                                    <AnimatePresence>
                                        {error && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: "auto" }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="mt-3"
                                            >
                                                <KitError>{error}</KitError>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    <div className="mt-3">
                                        <KitPrimaryButton onClick={onStart} disabled={isAnalyzing}>
                                            {isAnalyzing ? (
                                                <>
                                                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                                    <span>분석 중...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <SearchNormal1 size={19} variant="Linear" style={{ color: '#0A0A0A' }} />
                                                    <span>지금 바로 알아보기</span>
                                                </>
                                            )}
                                        </KitPrimaryButton>
                                    </div>

                                    <p className="mt-3 text-[12px] text-[#71717A]">
                                        인스타그램 아이디만 입력하면 바로 시작돼요
                                    </p>
                                </div>
                            )}
                        </motion.div>

                        {/* 가치 지표 3종 (2026-08-17 다니엘 지시 — 규모 숫자 전면 비노출).
                            이전에는 누적 분석·가입 회원·수집 계정 규모가 노출됐다: 사업 규모를 외부에
                            광고하는 것이라 제거. 아래는 전부 실측·상품 정의 근거 값이다:
                            · 1~2분 = 분석 소요 실측 37~106초(2026-08-10 직접 측정, HomePage 주석 참조)
                            · 24시간 = 연중무휴 자동 분석·즉시 발급(자동화 실동작. '추적' 표현은 위협적 어감이라 금지 — 다니엘 2026-08-17)
                            · 6가지 = 대시보드 제공 인사이트 실개수(방문자·가계정·스토리·관심사·연습·검색 VIP)
                            숫자를 바꿀 일이 있으면 반드시 실측·실기능 근거를 먼저 확보할 것 — 규모·이용자 수는 다시 넣지 않는다. */}
                        <motion.div {...rise(0.56)} className="mt-9 grid grid-cols-3 divide-x divide-white/[0.07] border-t border-white/[0.07] pt-6">
                            {[
                                { value: '1~2분', label: '평균 분석 완료' },
                                { value: '24시간', label: '신속 결과 발급' }, // '추적'은 위협적 어감 — 다니엘 교정 2026-08-17
                                { value: '6가지', label: '계정 인사이트' },
                            ].map((s, i) => (
                                <div key={s.label} className={`text-left ${i === 0 ? 'pr-3' : 'px-3'}`}>
                                    <p className="text-[26px] font-bold tracking-tight text-[#FAFAFA] sm:text-[32px]" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' }}>{statValue(s.value)}</p>
                                    <p className="mt-1 text-[12px] text-[#A1A1AA] sm:text-[13px]">{s.label}</p>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    {/* 우측 — 라이브 리포트 하이퍼프레임 (code-native, no video) — 이 화면의 주인공.
                        스튜디오처럼: 뒤에서 비추는 흰 둥근 빛 + 바닥 그림자(10/14 안내서 2-4 '3D 무대 바탕') */}
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 24, rotateX: -10 }}
                        animate={{ opacity: 1, y: 0, rotateX: 0 }}
                        transition={{ duration: 0.9, delay: 0.35, ease: EASE_OUT }}
                        style={{ transformPerspective: 1100 }}
                        className="relative"
                    >
                        <div
                            aria-hidden
                            className="pointer-events-none absolute left-1/2 top-[6%] h-[78%] w-[118%] -translate-x-1/2 rounded-full"
                            style={{ background: 'radial-gradient(closest-side, rgba(255,255,255,0.10), rgba(255,255,255,0.03) 55%, rgba(255,255,255,0) 100%)' }}
                        />
                        <div className="relative">
                            <VisitorReportHyperframe />
                        </div>
                        <div
                            aria-hidden
                            className="pointer-events-none mx-auto -mt-6 h-6 w-[58%] max-w-[260px] rounded-[50%]"
                            style={{ background: 'radial-gradient(closest-side, rgba(0,0,0,0.85), rgba(0,0,0,0))', filter: 'blur(6px)' }}
                        />
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default HeroSection;
